import type { Calendar, SyncDirection } from "@app/core";
import { createCalendar, softDeleteCalendar, updateCalendar } from "@app/core";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { useSettingsStore } from "./settings.store.js";
import { useTasksStore } from "./tasks.store.js";

interface CalendarsState {
  loaded: boolean;
  calendars: Calendar[];
  hydrate: () => Promise<void>;
  add: (name: string, color?: string | null) => Promise<Calendar>;
  rename: (id: string, name: string) => Promise<void>;
  setColor: (id: string, color: string | null) => Promise<void>;
  setHidden: (id: string, hidden: boolean) => Promise<void>;
  /** Bind an in-app calendar to a remote Google calendar (resets the sync cursor
   *  so the next reconcile does a full pull). Binding is user intent → it bumps
   *  `updated_at` and rides multi-device sync. */
  bindGoogle: (
    id: string,
    opts: { externalCalendarId: string; externalAccount: string; direction?: SyncDirection },
  ) => Promise<void>;
  /** Change just the sync direction of an already-bound calendar (keeps the cursor). */
  setSyncDirection: (id: string, direction: SyncDirection) => Promise<void>;
  /** Remove an external binding (also clears the device-local sync cursor). */
  unbindExternal: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

// Dedup concurrent hydrate() calls (React StrictMode double-invokes the mount
// effect) so the first-run bootstrap can't seed two default calendars in a race.
let inflight: Promise<void> | null = null;

export const useCalendarsStore = create<CalendarsState>((set, get) => ({
  loaded: false,
  calendars: [],

  hydrate() {
    if (inflight) return inflight;
    inflight = hydrateOnce(set).finally(() => { inflight = null; });
    return inflight;
  },

  async add(name, color = null) {
    const { calendars } = getServices();
    const c = createCalendar({ name, color });
    await calendars.insert(c);
    set({ calendars: [...get().calendars, c] });
    return c;
  },

  async rename(id, name) {
    await patch(set, get, id, { name });
  },

  async setColor(id, color) {
    await patch(set, get, id, { color });
  },

  async setHidden(id, hidden) {
    await patch(set, get, id, { hidden });
  },

  async bindGoogle(id, opts) {
    await patch(set, get, id, {
      external_calendar_id: opts.externalCalendarId,
      external_source: "google",
      external_account: opts.externalAccount,
      sync_direction: opts.direction ?? "both",
      sync_token: null,
      last_synced_at: null,
    });
  },

  async setSyncDirection(id, direction) {
    await patch(set, get, id, { sync_direction: direction });
  },

  async unbindExternal(id) {
    await patch(set, get, id, {
      external_calendar_id: null,
      external_source: null,
      external_account: null,
      sync_direction: "off",
      sync_token: null,
      last_synced_at: null,
    });
  },

  async remove(id) {
    const { calendars } = getServices();
    const current = get().calendars.find((c) => c.id === id);
    if (!current) return;
    // Deleting a calendar deletes the tasks it holds (they don't silently
    // migrate to another calendar). Cascade first, then drop the calendar.
    await useTasksStore.getState().removeByCalendar(id);
    await calendars.update(softDeleteCalendar(current));
    const remaining = get().calendars.filter((c) => c.id !== id);
    set({ calendars: remaining });
    // If the deleted calendar was the default, fall back to another (or null).
    const settings = useSettingsStore.getState();
    if (settings.defaultCalendarId === id) {
      await settings.setDefaultCalendarId(remaining[0]?.id ?? null);
    }
  },
}));

async function hydrateOnce(
  set: (partial: Partial<CalendarsState>) => void,
): Promise<void> {
  const { calendars } = getServices();
  let list = await calendars.listActive();
  // Bootstrap: every install needs at least one calendar so the form picker and
  // the settings default always resolve to something. Seed it on first run.
  if (list.length === 0) {
    const seeded = createCalendar({ name: "Personal" });
    await calendars.insert(seeded);
    list = [seeded];
  }
  set({ calendars: list, loaded: true });
  // Ensure the default calendar setting points at a calendar that still exists.
  const settings = useSettingsStore.getState();
  const current = settings.defaultCalendarId;
  if (!current || !list.some((c) => c.id === current)) {
    await settings.setDefaultCalendarId(list[0]!.id);
  }
}

async function patch(
  set: (partial: Partial<CalendarsState>) => void,
  get: () => CalendarsState,
  id: string,
  fields: Parameters<typeof updateCalendar>[1],
): Promise<void> {
  const { calendars } = getServices();
  const current = get().calendars.find((c) => c.id === id);
  if (!current) return;
  const next = updateCalendar(current, fields);
  await calendars.update(next);
  set({ calendars: get().calendars.map((c) => (c.id === id ? next : c)) });
}
